import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { BookOpen, FileText, Tablet } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const resolveFileUrl = (url) =>
  url && url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const bookNumber = (book) => String(book.order).padStart(2, '0');

const BookCover = ({ book, className }) =>
  book.cover_url ? (
    <img src={resolveFileUrl(book.cover_url)} alt={`${book.title} - book cover`} loading="lazy" decoding="async" className={className} />
  ) : (
    <div className="flex flex-col items-center justify-center text-center p-6 h-full w-full">
      <BookOpen className="h-12 w-12 text-brand-accent/70 mb-3" />
      <p className="font-heading text-white text-base leading-tight">{book.title}</p>
    </div>
  );

const BookTile = ({ book, onOpen }) => (
  <button
    onClick={() => onOpen(book)}
    className="group text-left bg-white border border-slate-200 rounded overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col"
    data-testid={`book-card-${book.order}`}
  >
    <div className="relative aspect-[3/4] bg-gradient-to-br from-brand-primary to-slate-900 flex items-center justify-center p-3 md:p-4 overflow-hidden">
      <span className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-brand-accent text-white text-[10px] md:text-xs font-semibold tracking-widest rounded">
        BOOK {bookNumber(book)}
      </span>
      <BookCover book={book} className="max-h-full max-w-full object-contain rounded shadow-lg group-hover:scale-[1.03] transition-transform duration-500" />
    </div>
    <div className="p-3 md:p-5 flex-grow">
      <h3 className="font-heading text-base md:text-xl font-semibold text-brand-primary leading-snug line-clamp-3" data-testid={`book-title-${book.order}`}>
        {book.title}
      </h3>
      <p className="mt-2 font-body text-xs md:text-sm font-medium text-brand-accent">View details & download</p>
    </div>
  </button>
);

const DownloadLink = ({ href, primary, icon: Icon, label, testId, download }) => (
  <a
    href={href}
    {...(download ? { download: true } : {})}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center gap-2 px-5 py-3 font-body font-medium text-sm rounded transition-colors duration-300 ${primary
      ? 'bg-brand-accent text-white hover:bg-brand-accent-hover'
      : 'bg-transparent border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
    data-testid={testId}
  >
    <Icon className="h-4 w-4" /> {label}
  </a>
);

const BookDialog = ({ book, onClose }) => {
  const pdf = resolveFileUrl(book?.pdf_url);
  const epub = resolveFileUrl(book?.epub_url);
  return (
    <Dialog open={!!book} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="book-dialog">
        {book && (
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-span-2 bg-gradient-to-br from-brand-primary to-slate-900 flex items-center justify-center p-6 min-h-[260px]">
              <BookCover book={book} className="max-h-80 w-auto object-contain rounded shadow-lg" />
            </div>
            <div className="md:col-span-3 p-6 md:p-8">
              <p className="text-xs font-semibold tracking-[0.25em] text-brand-accent mb-2">BOOK {bookNumber(book)}</p>
              <DialogTitle className="font-heading text-2xl md:text-3xl font-semibold text-brand-primary leading-tight mb-2" data-testid="book-dialog-title">
                {book.title}
              </DialogTitle>
              {book.subtitle && <p className="font-body text-sm font-medium text-brand-accent uppercase tracking-wider mb-4">{book.subtitle}</p>}
              <DialogDescription className="font-body text-base leading-relaxed text-slate-700 mb-6">
                {book.description}
              </DialogDescription>
              <div className="flex flex-wrap gap-3">
                {pdf && <DownloadLink href={pdf} download primary icon={FileText} label="Download Free PDF" testId={`download-pdf-${book.order}`} />}
                {epub && <DownloadLink href={epub} download primary icon={Tablet} label="Download Free eBook" testId={`download-epub-${book.order}`} />}
                {pdf && <DownloadLink href={pdf} icon={BookOpen} label="View PDF" testId={`view-pdf-${book.order}`} />}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Books = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/books`)
      .then(({ data }) => setBooks(data))
      .catch((err) => console.error('Failed to load books:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background-primary">
      <SEO
        title="Our Books | RestoreMyWord.net"
        description="Explore the Restore My Word book library. Discover biblical teachings and study resources, with free PDF downloads for readers, teachers, churches, and families."
        url="https://restoremyword.net/books"
      />
      <Navbar />

      <section className="py-12 md:py-16 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-brand-accent mb-3 block">FREE PDF LIBRARY</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white" data-testid="our-books-heading">
            Our Books
          </h1>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {loading && <p className="text-center font-body text-slate-600" data-testid="books-loading">Loading books...</p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8" data-testid="books-grid">
            {books.map((book) => <BookTile key={book.id} book={book} onOpen={setSelected} />)}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-brand-primary" data-testid="closing-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p
            className="font-body text-lg md:text-xl leading-relaxed text-white/95 text-center italic"
            data-testid="closing-message"
          >
            These books do not add to Scripture. They are starter tools so the hungry can take up the Sword again. Audio, print, and study pieces will follow as the morning watch permits. The remnant is often hidden inside existing fellowships. If you are one of them, the armory is open for your use.
          </p>
        </div>
      </section>

      <BookDialog book={selected} onClose={() => setSelected(null)} />
      <Footer />
    </div>
  );
};

export default Books;
